import React, { useState, useEffect } from 'react';
import { ArrowLeft, Filter, DownloadIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../services/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useFinancial } from '../../../contexts/FinancialContext';
import { formatCurrency } from '../../../utils/formatters';

export function CajaHistorialDetail() {
  const navigate = useNavigate();
  const { closingId } = useParams();
  const { user } = useAuth();
  const { business } = useFinancial();
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'income', 'expense', 'sale_payment'
  const [localBusinessId, setLocalBusinessId] = useState(null);

  useEffect(() => {
    console.log('CajaHistorialDetail - Inicializando localBusinessId');
    
    const getBusinessId = async () => {
      if (business?.id) {
        console.log('CajaHistorialDetail - Usando business.id del contexto:', business.id);
        setLocalBusinessId(business.id);
        return;
      }
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const businessId = session?.user?.user_metadata?.businessId;
        
        if (businessId) {
          console.log('CajaHistorialDetail - Usando businessId de sesión actual:', businessId);
          setLocalBusinessId(businessId);
          return;
        }
        
        console.log('CajaHistorialDetail - Usando businessId hardcodeado como último recurso');
        setLocalBusinessId('6ce03505-b504-44c6-a4ba-45eaf157aa5b');
      } catch (error) {
        console.error('Error obteniendo sesión:', error);
        setLocalBusinessId('6ce03505-b504-44c6-a4ba-45eaf157aa5b');
      }
    };
    
    getBusinessId();
  }, [business?.id]);

  useEffect(() => {
    if (closingId && localBusinessId) {
      fetchClosingDetail();
      fetchTransactions();
    }
  }, [closingId, localBusinessId]);

  const fetchClosingDetail = async () => {
    if (!localBusinessId || !closingId) return;
    
    try {
      setLoading(true);
      console.log('Consultando detalle del cierre:', closingId, 'con businessId:', localBusinessId);
      
      const { data, error } = await supabase
        .from('financial_closings')
        .select(`
          id,
          status,
          created_at,
          closed_at,
          opening_balance,
          closing_balance,
          created_by,
          closed_by,
          notes,
          discrepancy
        `)
        .eq('id', closingId)
        .eq('business_id', localBusinessId)
        .single();
      
      if (error) {
        console.error('Error al consultar cierre:', error);
        throw error;
      }
      
      console.log('Cierre encontrado:', data?.id);
      setClosing(data);
    } catch (error) {
      console.error('Error al cargar el detalle del cierre:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    if (!localBusinessId || !closingId) return;
    
    try {
      setLoadingTransactions(true);
      console.log('Consultando transacciones para el cierre:', closingId);
      
      const { data, error } = await supabase
        .from('financial_transactions')
        .select(`
          id,
          type,
          amount,
          payment_method,
          description,
          created_at,
          created_by
        `)
        .eq('closing_id', closingId)
        .eq('business_id', localBusinessId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error al consultar transacciones:', error);
        throw error;
      }
      
      console.log('Transacciones encontradas:', data?.length || 0);
      setTransactions(data || []);
    } catch (error) {
      console.error('Error al cargar las transacciones:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Calcular totales de un cierre
  const calculateTotal = (balances) => {
    if (!balances) return 0;
    
    let total = 0;
    if (balances.cash) total += balances.cash;
    if (balances.transfer) total += balances.transfer;
    if (balances.credit) total += balances.credit;
    if (balances.other) total += balances.other;
    
    return total;
  };

  // Filtrar transacciones
  const filteredTransactions = transactions.filter(transaction => {
    if (filter === 'all') return true;
    return transaction.type === filter;
  });

  // Calcular totales de las transacciones filtradas
  const calculateTransactionTotals = () => {
    const totals = {
      income: 0,
      expense: 0,
      sale_payment: 0
    };
    
    filteredTransactions.forEach(transaction => {
      if (transaction.type in totals) {
        totals[transaction.type] += transaction.amount || 0;
      }
    });
    
    return totals;
  };
  
  const transactionTotals = calculateTransactionTotals();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
          <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
          <div className="space-y-2">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!closing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border dark:border-gray-700 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No se encontró el cierre de caja
          </p>
          <button
            onClick={() => navigate('/caja/historial')}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            Volver al historial
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 z-10">
        <div className="p-4">
          {/* Back button and title */}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/caja/historial')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-900 dark:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Detalle de Caja
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {new Date(closing.closed_at).toLocaleDateString('es-AR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Resumen */}
      <div className="p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Resumen
          </h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Apertura</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatCurrency(closing.opening_balance?.cash || 0)}
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Cierre</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatCurrency(closing.closing_balance?.cash || 0)}
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {Math.abs(closing.discrepancy?.cash || 0) > 0 ? 'Discrepancia' : 'Cuadre'}
              </div>
              <div className={`text-lg font-semibold ${
                (closing.discrepancy?.cash || 0) === 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {formatCurrency(closing.discrepancy?.cash || 0)}
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total final</div>
              <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                {formatCurrency(calculateTotal(closing.closing_balance))}
              </div>
            </div>
          </div>
          
          <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
            <div className="flex justify-between items-center mb-1">
              <span>Abierta:</span>
              <span>{new Date(closing.created_at).toLocaleString('es-AR')}</span>
            </div>
            <div className="flex justify-between items-center mb-1">
              <span>Cerrada:</span>
              <span>{new Date(closing.closed_at).toLocaleString('es-AR')}</span>
            </div>
          </div>
          
          {closing.notes && (
            <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-sm text-gray-600 dark:text-gray-300">
              <div className="font-medium mb-1">Notas:</div>
              <div className="italic">{closing.notes}</div>
            </div>
          )}
        </div>
        
        {/* Filtro de movimientos */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Movimientos
          </h2>
          
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg px-2 py-1 text-sm"
            >
              <option value="all">Todos</option>
              <option value="income">Ingresos</option>
              <option value="expense">Egresos</option>
              <option value="sale_payment">Cobros</option>
            </select>
          </div>
        </div>
        
        {/* Totales de movimientos filtrados */}
        {filter !== 'all' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm mb-4">
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Total {filter === 'income' ? 'ingresos' : 
                      filter === 'expense' ? 'egresos' : 
                      'cobros'}:
              </div>
              <div className={`text-lg font-bold ${
                filter === 'expense' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
              }`}>
                {formatCurrency(transactionTotals[filter])}
              </div>
            </div>
          </div>
        )}
        
        {/* Lista de transacciones */}
        <div className="space-y-3 pb-24">
          {loadingTransactions ? (
            <div className="animate-pulse space-y-3">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="h-20 bg-white dark:bg-gray-800 rounded-lg shadow-sm"></div>
              ))}
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border dark:border-gray-700 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                No hay movimientos {filter !== 'all' ? 'de este tipo' : ''} para este cierre de caja
              </p>
            </div>
          ) : (
            <>
              {filteredTransactions.map(transaction => (
                <div key={transaction.id} className="w-full bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base font-bold text-gray-900 dark:text-white truncate">
                          {transaction.type === 'income' ? 'Ingreso' : 
                           transaction.type === 'expense' ? 'Egreso' : 
                           transaction.type === 'sale_payment' ? 'Pago de venta' : 
                           'Movimiento'}
                        </span>
                        <span className={`shrink-0 px-1.5 py-0.5 ${
                          transaction.type === 'income' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 
                          transaction.type === 'expense' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 
                          'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        } text-xs font-medium rounded`}>
                          {transaction.payment_method === 'cash' ? 'Efectivo' : 
                           transaction.payment_method === 'transfer' ? 'Transferencia' : 
                           transaction.payment_method}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {transaction.description}
                      </div>
                      
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {new Date(transaction.created_at).toLocaleString('es-AR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    
                    <div className="text-right shrink-0">
                      <div className={`text-lg font-bold ${
                        transaction.type === 'income' ? 'text-green-600 dark:text-green-400' : 
                        transaction.type === 'expense' ? 'text-red-600 dark:text-red-400' : 
                        'text-gray-900 dark:text-white'
                      }`}>
                        {transaction.type === 'income' ? '+' : transaction.type === 'expense' ? '-' : ''}
                        ${transaction.amount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
} 